/**
 * Add or edit a site note: pick project (today first, then upcoming, then past) → note → photos → save.
 * New notes keep text + project as a local draft across refreshes; edit mode loads
 * the saved note, lets you drop existing photos and add new ones.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useFormDraft } from '../../hooks/useFormDraft';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { AuthImg } from '../../components/portal/AuthImg';
import { Camera, ImagePlus, Loader2, X, Check } from 'lucide-react';
import { jobCode, isConstruction } from '../../utils/jobCode';
import { format, parseISO } from 'date-fns';

const MAX_PHOTOS = 8;
const GROUPS = [['today', "Today's job"], ['upcoming', 'Upcoming'], ['past', 'Past jobs'], ['other', 'Also assigned']];
const dateHint = (p) => {
  try {
    if (p.group === 'upcoming' && p.next_date) return `Next: ${format(parseISO(p.next_date), 'EEE d MMM')}`;
    if (p.group === 'past' && p.last_date) return `Last: ${format(parseISO(p.last_date), 'EEE d MMM')}`;
  } catch { /* ignore */ }
  return '';
};

export function PortalSiteNoteCreate() {
  const { api, features } = useAuth();
  const navigate = useNavigate();
  const { noteId } = useParams();
  const [params] = useSearchParams();
  const editing = !!noteId;
  const [projects, setProjects] = useState(null);
  const [draft, setDraft, clearDraft, restored] = useFormDraft('site-note', { project_id: params.get('project') || '', note: '' });
  const [edit, setEdit] = useState(null); // { project_id, note, photos:[{idx}], removed:Set }
  const [photos, setPhotos] = useState([]); // new files { file, url }
  const [saving, setSaving] = useState(false);
  const camRef = useRef(null); const libRef = useRef(null);

  const form = editing ? edit : draft;
  const setForm = editing ? setEdit : setDraft;

  useEffect(() => {
    api.get('/portal/site-notes/projects').then(r => setProjects(r.data || [])).catch(() => { setProjects([]); toast.error('Could not load your projects'); });
  }, [api]);
  useEffect(() => {
    if (!editing) return;
    api.get(`/portal/site-notes/${noteId}`).then(r => {
      if (!r.data.can_edit) { toast.error('You can only edit your own notes'); navigate('/portal/site-notes', { replace: true }); return; }
      setEdit({ project_id: r.data.project_id, note: r.data.note || '', photos: r.data.photos || [], removed: [] });
    }).catch(() => { toast.error('Note not found'); navigate('/portal/site-notes', { replace: true }); });
  }, [api, noteId, editing, navigate]);
  useEffect(() => { const p = params.get('project'); if (p && !editing) setDraft(f => ({ ...f, project_id: p })); }, [params, setDraft, editing]);
  // Today's job is pre-selected when nothing else was chosen
  useEffect(() => {
    if (editing || !projects?.length || params.get('project')) return;
    const today = projects.find(p => p.is_today);
    if (today) setDraft(f => (f.project_id && projects.some(p => p.id === f.project_id) ? f : { ...f, project_id: today.id }));
  }, [projects, editing, params, setDraft]);
  useEffect(() => () => photos.forEach(p => URL.revokeObjectURL(p.url)), []); // eslint-disable-line react-hooks/exhaustive-deps

  const project = useMemo(() => projects?.find(p => p.id === form?.project_id), [projects, form?.project_id]);
  const keptExisting = editing && edit ? edit.photos.filter(p => !edit.removed.includes(p.idx)) : [];
  const total = keptExisting.length + photos.length;

  const addFiles = (list) => {
    const files = Array.from(list || []).filter(f => f.type.startsWith('image/') || /\.(heic|heif)$/i.test(f.name));
    if (!files.length) return;
    setPhotos(prev => {
      const room = MAX_PHOTOS - keptExisting.length - prev.length;
      if (room <= 0) { toast.error(`Up to ${MAX_PHOTOS} photos`); return prev; }
      return [...prev, ...files.slice(0, room).map(file => ({ file, url: URL.createObjectURL(file) }))];
    });
  };
  const removePhoto = (i) => setPhotos(prev => { URL.revokeObjectURL(prev[i].url); return prev.filter((_, j) => j !== i); });
  const toggleExisting = (idx) => setEdit(e => ({ ...e, removed: e.removed.includes(idx) ? e.removed.filter(x => x !== idx) : [...e.removed, idx] }));

  const save = async () => {
    if (!editing && !form.project_id) { toast.error('Pick a project'); return; }
    if (!form.note.trim() && total === 0) { toast.error('Add a note or a photo'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('note', form.note);
      photos.forEach(p => fd.append('photos', p.file, p.file.name || 'photo.jpg'));
      if (editing) {
        fd.append('remove_idx', edit.removed.join(','));
        await api.put(`/portal/site-notes/${noteId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Note updated');
      } else {
        fd.append('project_id', form.project_id);
        await api.post('/portal/site-notes', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Site note saved'); clearDraft();
      }
      photos.forEach(p => URL.revokeObjectURL(p.url));
      navigate('/portal/site-notes', { replace: true });
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save the note'); }
    finally { setSaving(false); }
  };

  if (editing && !edit) return <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" /></div>;

  return (
    <div className="p-4 space-y-4" data-testid={editing ? 'site-note-edit' : 'site-note-create'}>
      {!editing && restored && form.note && <div className="text-[11px] text-[#F5A623] bg-[#F5A623]/10 border border-[#F5A623]/20 rounded-lg px-3 py-2" data-testid="draft-restored">Picked up where you left off — your unsaved note was kept.</div>}

      <section className="space-y-2">
        <h2 className="text-white font-semibold">{editing ? 'Project' : 'Which project?'}</h2>
        {projects === null ? <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" /></div> : editing ? (
          <div className="rounded-xl border border-white/10 bg-[#2A3240] p-3 flex items-center gap-3" data-testid="edit-project">
            <span className={`font-bold text-sm ${isConstruction(project) ? 'text-blue-400' : 'text-[#F5A623]'}`}>{jobCode(project, features) || '—'}</span>
            <span className="text-white text-sm truncate">{project?.address || project?.project_ref || 'Project'}</span>
          </div>
        ) : projects.length === 0 ? (
          <div className="text-sm text-gray-400 rounded-xl border border-dashed border-white/15 p-4 text-center" data-testid="no-projects">You're not on any projects yet — ask the office to schedule you.</div>
        ) : (
          <div className="space-y-3" data-testid="project-options">
            {GROUPS.map(([key, label]) => {
              const list = projects.filter(p => p.group === key);
              if (!list.length) return null;
              const isToday = key === 'today';
              return (
                <div key={key} data-testid={`project-group-${key}`}>
                  <div className={`text-[11px] font-bold uppercase tracking-wider mb-1.5 ${isToday ? 'text-[#F5A623]' : 'text-gray-500'}`}>{label}</div>
                  <div className={`space-y-1.5 ${isToday ? '' : 'max-h-56 overflow-y-auto pr-0.5'}`}>
                    {list.map(p => {
                      const selected = form.project_id === p.id;
                      return (
                        <button key={p.id} type="button" onClick={() => setForm(f => ({ ...f, project_id: p.id }))}
                          className={`w-full text-left rounded-xl border flex items-center gap-3 active:scale-[0.99] ${isToday ? 'p-4' : 'p-3'} ${selected ? 'border-[#F5A623] bg-[#F5A623]/10' : isToday ? 'border-[#F5A623]/40 bg-[#2A3240]' : 'border-white/10 bg-[#2A3240]'}`} data-testid={`project-option-${p.id}`}>
                          <span className={`font-bold shrink-0 ${isToday ? 'text-base w-20' : 'text-sm w-16'} ${isConstruction(p) ? 'text-blue-400' : 'text-[#F5A623]'}`}>{jobCode(p, features)}</span>
                          <span className="flex-1 min-w-0">
                            <span className={`block text-white truncate ${isToday ? 'text-base font-semibold' : 'text-sm'}`}>{p.address || p.project_ref}</span>
                            <span className="block text-[11px] text-gray-500">{[p.postcode, dateHint(p)].filter(Boolean).join(' · ')}</span>
                          </span>
                          {selected && <Check className="w-5 h-5 text-[#F5A623] shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-white font-semibold">Note</h2>
        <Textarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="What's happening on site? e.g. “First fix complete upstairs, waiting on plasterboard delivery”" className="bg-[#2A3240] border-white/20 text-white min-h-[110px]" data-testid="site-note-text" />
      </section>

      <section className="space-y-2">
        <h2 className="text-white font-semibold">Photos <span className="text-gray-500 text-sm font-normal">({total}/{MAX_PHOTOS})</span></h2>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={() => camRef.current?.click()} disabled={total >= MAX_PHOTOS} className="h-14 border-white/20 text-white bg-white/5" data-testid="photo-camera-btn"><Camera className="w-5 h-5 mr-2" /> Take photo</Button>
          <Button variant="outline" onClick={() => libRef.current?.click()} disabled={total >= MAX_PHOTOS} className="h-14 border-white/20 text-white bg-white/5" data-testid="photo-library-btn"><ImagePlus className="w-5 h-5 mr-2" /> From library</Button>
        </div>
        <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => { addFiles(e.target.files); e.target.value = ''; }} data-testid="photo-camera-input" />
        <input ref={libRef} type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={e => { addFiles(e.target.files); e.target.value = ''; }} data-testid="photo-library-input" />
        {(editing && edit.photos.length > 0) || photos.length > 0 ? (
          <div className="grid grid-cols-3 gap-2" data-testid="photo-previews">
            {editing && edit.photos.map(p => {
              const removed = edit.removed.includes(p.idx);
              return (
                <div key={`e-${p.idx}`} className={`relative aspect-square rounded-xl overflow-hidden bg-black/30 border ${removed ? 'border-red-500/50 opacity-40' : 'border-white/10'}`} data-testid={`existing-photo-${p.idx}`}>
                  <AuthImg src={`/portal/site-notes/${noteId}/photos/${p.idx}?thumb=true`} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => toggleExisting(p.idx)} className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center" data-testid={`existing-photo-toggle-${p.idx}`}>{removed ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}</button>
                  {removed && <div className="absolute inset-x-0 bottom-0 bg-red-500/80 text-white text-[10px] text-center py-0.5">will be removed</div>}
                </div>
              );
            })}
            {photos.map((p, i) => (
              <div key={p.url} className="relative aspect-square rounded-xl overflow-hidden bg-black/30 border border-white/10">
                <img src={p.url} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removePhoto(i)} className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center" data-testid={`photo-remove-${i}`}><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        ) : null}
      </section>

      <div className="sticky bottom-24 pt-2">
        <Button onClick={save} disabled={saving || (!editing && !form.project_id) || (!form.note.trim() && total === 0)} className="w-full h-14 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-bold text-base shadow-xl" data-testid="site-note-save-btn">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : editing ? 'Save changes' : <>Save to {project ? jobCode(project, features) : 'project'}</>}
        </Button>
      </div>
    </div>
  );
}
